import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
export const EXPIRED_FLAG = "kd_session_expired";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    localStorage.removeItem("token");
    refresh();
  }, []);

  const expire = useCallback(() => {
    setUser((u) => {
      if (u) sessionStorage.setItem(EXPIRED_FLAG, "1");
      return null;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("auth:expired", expire);
    return () => window.removeEventListener("auth:expired", expire);
  }, [expire]);

  useEffect(() => {
    if (!user?.session_expires_at) return undefined;
    const ms = new Date(user.session_expires_at).getTime() - Date.now();
    const id = setTimeout(expire, Math.max(0, ms));
    return () => clearTimeout(id);
  }, [user, expire]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    sessionStorage.removeItem(EXPIRED_FLAG);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
